import { Divider, Group, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';
import { useCallback } from 'react';

import { AddIcon, DownloadIcon, ExportIcon, Lock, Unlock, Upload } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableDownload } from '@apple/ui/data-table/hooks/use-table-download';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';
import { ExcelUploadDialog } from '@apple/utils/files/components/ExcelUploadDialog';
import { useFileDownload } from '@apple/utils/files/hooks/useFileDownload';
import { FormattedBoolean } from '@apple/utils/globalization/components/FormattedBoolean';
import { FormattedDate } from '@apple/utils/globalization/components/FormattedDate';
import { FormattedPrice } from '@apple/utils/globalization/components/FormattedPrice';

import { useUserRoles } from '@apple/features/auth/hooks/useUserRoles';
import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { userQueryKeys } from '@apple/features/user/queries';
import {
	deleteUsers,
	getUserManagementTemplateUrl,
	saveUsers,
	uploadUsers,
} from '@apple/features/user-channel/api/management';
import type { UserFilters, UserListing } from '@apple/features/user-channel/models/listing';
import { getUserDataTableQueryOptions } from '@apple/features/user-channel/queries/odata';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
});

export const Route = createFileRoute('/_authed/_admin/manage/users')({
	beforeLoad: args =>
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.UserManagement.Read',
		}),
	component: UserManagementRoute,
});

const defaultFilters: UserFilters = {
	isActive: 'active',
};

function UserManagementRoute() {
	const { t } = useTranslation('manage');
	const [uploadOpen, uploadActions] = useDisclosure(false);
	const excelTemplate = useFileDownload({
		method: 'get',
		url: getUserManagementTemplateUrl(),
		fallbackFilename: 'users.xlsx',
		errorTitle: t('common:error.title'),
		errorMessage: t('common:error.fileDownload'),
	});
	const { queryClient } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();
	const userRoles = useUserRoles();

	const tableState = useTableState<UserListing, UserFilters>({
		search,
		navigate,
		defaultFilters: defaultFilters,
		defaultSorting: [{ id: 'Username', desc: false }],
		fieldMap: [],
	});

	const usersQuery = useQuery(
		getUserDataTableQueryOptions({
			filters: tableState.currentFilterData,
			pagination: tableState.state.pagination,
			commonFilters: {},
		}),
	);

	const table = useTable({
		features,
		data: usersQuery.data.rows,
		rowCount: usersQuery.data.totalRowCount,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		manualFiltering: true,
		manualSorting: true,
		manualPagination: true,
		meta: {
			getFacetedUniqueValues: columnId => {
				switch (columnId) {
					case 'userRoles':
						return new Map(
							!userRoles.loading && !userRoles.error
								? userRoles.roles?.map(x => [x.name, 1])
								: [],
						);
					case 'isActive':
						return new Map([undefined, 'active', 'inactive'].map(x => [x, 1]));
					case 'isLocked':
						return new Map([undefined, 'locked', 'unlocked'].map(x => [x, 1]));
					default:
						return undefined;
				}
			},
		},
		columnResizeMode: 'onChange',
		columns: [
			{
				id: 'username',
				accessorKey: 'username',
				header: t('users.fields.userName'),
				filter: {
					group: t('users.filterGroups.details'),
					label: t('users.filters.nameOrEmail'),
					sortOrder: 1,
				},
				cell: ({ row }) => (
					<Link
						to='/manage/users/$userName'
						state={{ from: location }}
						params={{ userName: row.original.username }}
					>
						<Text size='sm'>{row.original.username}</Text>
					</Link>
				),
			},
			{
				id: 'email',
				accessorKey: 'email',
				header: t('users.fields.emailAddress'),
				enableColumnFilter: false,
			},
			{
				id: 'firstName',
				accessorKey: 'firstName',
				header: t('users.fields.firstName'),
				enableColumnFilter: false,
			},
			{
				id: 'lastName',
				accessorKey: 'lastName',
				header: t('users.fields.lastName'),
				enableColumnFilter: false,
			},
			{
				id: 'userRoles',
				accessorKey: 'userRoles',
				header: t('users.fields.userRole'),
				filter: {
					group: t('users.filterGroups.details'),
					variant: 'select',
					sortOrder: 2,
				},
			},
			{
				id: 'orderValueLimit',
				accessorKey: 'orderValueLimit',
				header: t('users.fields.orderValueLimit'),
				cell: ({ row }) => (
					<FormattedPrice value={Number(row.original.orderValueLimit)} currency={'USD'} />
				),
				filter: {
					group: t('users.filterGroups.details'),
					variant: 'number-range',
					sortOrder: 3,
				},
			},
			{
				id: 'isActive',
				accessorKey: 'isActive',
				header: t('users.fields.userStatus'),
				cell: ({ row }) => (
					<Group>
						<FormattedBoolean value={row.original.isActive} useIcon />
					</Group>
				),
				filter: {
					group: t('users.filterGroups.details'),
					variant: 'segmented-switch',
					sortOrder: 11,
					getFilterDisplayValue: x => {
						switch (x) {
							case 'active':
								return t('user:profile.labels.active');
							case 'inactive':
								return t('user:profile.labels.inactive');
							default:
								return t('common:label.all');
						}
					},
				},
			},
			{
				id: 'isLocked',
				accessorKey: 'isLocked',
				header: t('users.fields.locked'),
				size: 100,
				cell: ({ row }) => (
					<Group>{row.original.isLocked ? <Lock c='red' /> : <Unlock c='green' />}</Group>
				),
				filter: {
					group: t('users.filterGroups.details'),
					variant: 'segmented-switch',
					sortOrder: 11,
					getFilterDisplayValue: x => {
						switch (x) {
							case 'locked':
								return t('common:label.yes');
							case 'unlocked':
								return t('common:label.no');
							default:
								return t('common:label.all');
						}
					},
				},
			},
			{
				id: 'lastSignInDate',
				accessorKey: 'lastSignInDate',
				header: t('users.fields.lastSignInDate'),
				filter: {
					group: t('users.filterGroups.details'),
					variant: 'date-range',
					sortOrder: 4,
				},
				cell: ({ row }) =>
					row.original.lastSignInDate && (
						<FormattedDate value={row.original.lastSignInDate} humanize />
					),
			},
			{
				id: 'lastOrderDate',
				accessorKey: 'lastOrderDate',
				header: t('users.fields.lastOrderDate'),
				size: 100,
				filter: {
					group: t('users.filterGroups.details'),
					variant: 'date-range',
					sortOrder: 4,
				},
				cell: ({ row }) =>
					row.original.lastOrderDate && (
						<FormattedDate value={row.original.lastOrderDate} humanize />
					),
			},
			{
				id: 'createDate',
				accessorKey: 'createDate',
				header: t('users.fields.createdDate'),
				filter: {
					group: t('users.filterGroups.details'),
					variant: 'date-range',
					sortOrder: 5,
				},
				cell: ({ row }) =>
					row.original.createDate && <FormattedDate value={row.original.createDate} />,
			},
			{
				id: 'activeDate',
				accessorKey: 'activeDate',
				header: t('users.fields.activeDate'),
				cell: ({ row }) =>
					row.original.activeDate && <FormattedDate value={row.original.activeDate} />,
				enableColumnFilter: false,
			},
			{
				id: 'inactiveDate',
				accessorKey: 'inactiveDate',
				header: t('users.fields.inactiveDate'),
				cell: ({ row }) =>
					row.original.inactiveDate && (
						<FormattedDate value={row.original.inactiveDate} />
					),
				enableColumnFilter: false,
			},
		],
	});

	const tableDownload = useTableDownload({
		table,
		queryOptionsFn: getUserDataTableQueryOptions,
	});

	const handleExport = useCallback(() => {
		void tableDownload.download({
			filters: tableState.currentFilterData,
			fileName: 'Users',
		});
	}, [tableDownload, tableState.currentFilterData]);

	const handleUploadConfirmed = useCallback(async () => {
		await queryClient.resetQueries({
			queryKey: userQueryKeys.all,
		});
		await usersQuery.refetch();
	}, [queryClient, usersQuery]);

	return (
		<TableLayout
			title={t('users.title')}
			table={table}
			toolbarButtons={[
				<ToolbarButton
					key='add'
					tooltip={t('users.buttons.add.tooltip')}
					icon={AddIcon}
					onClick={() =>
						void navigate({
							to: '/manage/users/add',
							state: { from: location },
						})
					}
					data-testid='add-user'
				/>,
				<Divider key='divider-1' orientation='vertical' mx='xs' />,
				<ToolbarButton
					key='download'
					tooltip={t('users.buttons.download.tooltip')}
					icon={DownloadIcon}
					loading={excelTemplate.downloading}
					onClick={() => void excelTemplate.download()}
				/>,
				<ToolbarButton
					key='upload'
					tooltip={t('users.buttons.upload.tooltip')}
					icon={Upload}
					disabled={uploadOpen}
					onClick={uploadActions.open}
				/>,
				<Divider key='divider-2' orientation='vertical' mx='xs' />,
				<ToolbarButton
					key='export'
					loading={tableDownload.downloading}
					tooltip={t('users.buttons.export.tooltip')}
					icon={ExportIcon}
					onClick={handleExport}
				/>,
			]}
		>
			<DataTable table={table} minWidth='3000px' loading={usersQuery.isFetching} />
			<ExcelUploadDialog
				keyProperty={'username'}
				keyLabel={t('users.fields.userName')}
				opened={uploadOpen}
				close={uploadActions.close}
				onUpload={uploadUsers}
				onSave={users => saveUsers(users, 'N/A')}
				onDelete={usernames => deleteUsers(usernames, 'N/A')}
				onConfirmed={handleUploadConfirmed}
			/>
		</TableLayout>
	);
}
